import React, { useState } from "react";
import CalculatorPage from "./CalculatorPage";

const MODES = [
  { id: "of", label: "% of a number" },
  { id: "isWhat", label: "X is what % of Y" },
  { id: "change", label: "% change" },
];

function PercentageCalculator() {
  const [mode, setMode] = useState("of");
  const [a, setA] = useState("");
  const [b, setB] = useState("");
  const [result, setResult] = useState(null);
  const [error, setError] = useState("");

  const reset = (nextMode) => {
    setMode(nextMode);
    setA("");
    setB("");
    setResult(null);
    setError("");
  };

  const onInput = (setter) => (e) => {
    setter(e.target.value);
    setResult(null);
    setError("");
  };

  const calculate = () => {
    const x = parseFloat(a);
    const y = parseFloat(b);
    if (isNaN(x) || isNaN(y)) {
      setError("Please fill in both values.");
      setResult(null);
      return;
    }

    setError("");
    if (mode === "of") {
      setResult(
        <>
          <strong>{x}%</strong> of <strong>{y}</strong> is{" "}
          <strong>{((x / 100) * y).toLocaleString(undefined, { maximumFractionDigits: 4 })}</strong>
        </>
      );
    } else if (mode === "isWhat") {
      if (y === 0) {
        setError("The second value can't be zero.");
        return;
      }
      setResult(
        <>
          <strong>{x}</strong> is{" "}
          <strong>{((x / y) * 100).toLocaleString(undefined, { maximumFractionDigits: 2 })}%</strong>{" "}
          of <strong>{y}</strong>
        </>
      );
    } else {
      if (x === 0) {
        setError("The starting value can't be zero for percentage change.");
        return;
      }
      const change = ((y - x) / Math.abs(x)) * 100;
      setResult(
        <>
          From <strong>{x}</strong> to <strong>{y}</strong> is a{" "}
          <strong>
            {Math.abs(change).toLocaleString(undefined, { maximumFractionDigits: 2 })}%{" "}
            {change >= 0 ? "increase" : "decrease"}
          </strong>
        </>
      );
    }
  };

  const labels =
    mode === "of"
      ? ["Percentage (%)", "Number"]
      : mode === "isWhat"
      ? ["Value (X)", "Out of (Y)"]
      : ["Starting value", "Final value"];

  return (
    <CalculatorPage
      title="Percentage Calculator"
      metaDescription="Free percentage calculator: find X% of a number, work out what percent one number is of another, or calculate percentage increase and decrease."
      intro={
        <>
          Solve the three most common <strong>percentage problems</strong>:
          percent of a number, what percent X is of Y, and percentage change.
        </>
      }
      seoContent={
        <>
          <h2>How percentages work</h2>
          <p>
            A percentage is just a fraction of 100. To find X% of a number,
            multiply the number by X and divide by 100 — so 25% of 80 is
            (25 × 80) ÷ 100 = 20. To find what percent X is of Y, divide X by
            Y and multiply by 100 — so 20 out of 80 is (20 ÷ 80) × 100 = 25%.
          </p>
          <h2>Percentage increase and decrease</h2>
          <p>
            Percentage change compares a new value to a starting value:
            (new − old) ÷ old × 100. If a price goes from 200 to 250, that's
            a 25% increase; if it drops from 250 to 200, that's a 20% decrease.
            Note the asymmetry — the direction matters because the starting
            point is different.
          </p>
          <h2>Frequently asked questions</h2>
          <h3>How do I find what percentage one number is of another?</h3>
          <p>
            Divide the first number by the second and multiply by 100. For
            example, 30 out of 120 is (30 ÷ 120) × 100 = 25%.
          </p>
          <h3>How do I add a percentage to a number?</h3>
          <p>
            Work out the percentage of the number, then add it on. To add 15%
            to 200, calculate 15% of 200 (which is 30) and add it, giving 230.
          </p>
          <h3>Why isn't a 50% drop cancelled by a 50% rise?</h3>
          <p>
            Because each percentage is taken from a different base. A 50% fall
            from 100 gives 50, and a 50% rise from 50 only returns to 75 — the
            increase is calculated on the smaller number.
          </p>
        </>
      }
    >
      <div className="mode-tabs">
        {MODES.map((m) => (
          <button
            key={m.id}
            className={`mode-tab ${mode === m.id ? "active" : ""}`}
            onClick={() => reset(m.id)}
          >
            {m.label}
          </button>
        ))}
      </div>

      <div className="fields">
        <label className="field">
          <span>{labels[0]}</span>
          <input type="number" value={a} onChange={onInput(setA)} placeholder="e.g. 25" />
        </label>
        <label className="field">
          <span>{labels[1]}</span>
          <input type="number" value={b} onChange={onInput(setB)} placeholder="e.g. 80" />
        </label>
      </div>

      <button className="btn" onClick={calculate}>Calculate</button>

      {error && <div className="error">{error}</div>}
      {result && <div className="result">{result}</div>}
    </CalculatorPage>
  );
}

export default PercentageCalculator;
